/**
 * useDelegatedTaskStatus
 *
 * Tracks a single delegated run's progress in real-time by subscribing
 * to agent.run.* events via WebSocket. Used by AuiDelegatedTaskCard to
 * show live activity without polling.
 *
 * Falls back to the surface-provided status when no events are received
 * (cold-load scenario).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { SubscribeFn } from '../contexts/PlatformEventContext';
import type { PlatformEvent, TokenUsage } from '../types/events';
import type { DelegatedTask, DelegatedTaskStatus, DelegatedTaskSummary, TaskActivityPhase, DelegatedTimelineEntry } from '../types/delegated-tasks';
import { getToolDetail } from '../utils/tool-details';
import { toolCallEntryLabel, type SubAgentChild } from '../components/assistant-ui/cot-timeline';

export interface DelegatedTaskLiveStatus {
  /** Derived status from events, or null if no events received yet */
  status: DelegatedTaskStatus | null;
  /** Current activity phase of the delegated run */
  activityPhase: TaskActivityPhase;
  /** Human-readable activity description (e.g. reasoning text, tool name) */
  activityText: string | null;
  /** Number of tool calls made so far */
  toolCallCount: number;
  /** Elapsed time in ms since run started */
  elapsedMs: number | null;
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Accumulated CoT timeline entries for the rolling display */
  timelineEntries: DelegatedTimelineEntry[];
  /** Result summary text when the run completes, or null */
  resultSummary: string | null;
  /** Token usage stats (cumulative) */
  usage: TokenUsage | null;
  /** True once the status has been resolved (from REST or WS). False during initial loading. */
  resolved: boolean;
  /** Timestamp (ms) of the last event received, or null */
  lastEventAt: number | null;
}

/** Max timeline entries kept in state to prevent unbounded growth */
const MAX_TIMELINE_ENTRIES = 50;

/**
 * Module-level registry of live task statuses, keyed by delegatedRunId.
 * Cards write their effective status here so the Grid can sort without
 * needing a shared context.
 */
export const delegatedTaskStatusRegistry = new Map<string, DelegatedTaskStatus>();

/**
 * Pre-fetched summaries for terminal tasks, keyed by delegatedRunId.
 * Populated by useConversationRestore, consumed here to skip individual getRunStatus() calls.
 */
export const delegatedTaskSummaryStore = new Map<string, DelegatedTaskSummary & { status: DelegatedTaskStatus; updatedAt?: string }>();

/**
 * Cached list of delegated tasks per conversation. Survives component
 * remounts so AuiTaskDashboard can hydrate immediately and refresh in the
 * background instead of flashing an empty grid every time the user
 * navigates away and back.
 */
export const delegatedTasksListCache = new Map<string, DelegatedTask[]>();

export function removeTaskFromListCache(conversationId: string, taskId: string): void {
  const list = delegatedTasksListCache.get(conversationId);
  if (!list) return;
  const filtered = list.filter((t) => t.taskId !== taskId);
  if (filtered.length !== list.length) {
    delegatedTasksListCache.set(conversationId, filtered);
  }
}

/** Full InternalState cache — survives component unmount/remount */
const delegatedTaskStateCache = new Map<string, InternalState>();

/** Listener for mounted components to receive reactive state updates */
type StateListener = (state: InternalState) => void;
const stateListeners = new Map<string, StateListener>();

/** Background subscription tracking */
interface BackgroundSub {
  refCount: number;
  unsubscribe: (() => void) | null;
  subscribeFn: SubscribeFn;
}
const backgroundSubs = new Map<string, BackgroundSub>();

function isTerminalStatus(s: DelegatedTaskStatus): boolean {
  return s === 'completed' || s === 'failed' || s === 'cancelled';
}

function createBackgroundHandler(delegatedRunId: string) {
  return (event: PlatformEvent) => {
    const prev = delegatedTaskStateCache.get(delegatedRunId) ?? INITIAL_STATE;
    const next = applyEvent(prev, event);
    delegatedTaskStateCache.set(delegatedRunId, next);
    if (next.status) {
      delegatedTaskStatusRegistry.set(delegatedRunId, next.status);
    }
    // Notify mounted component listener
    stateListeners.get(delegatedRunId)?.(next);
    // Auto-cleanup terminal background subs with no active listeners
    if (next.status && isTerminalStatus(next.status)) {
      const sub = backgroundSubs.get(delegatedRunId);
      if (sub && sub.refCount <= 0) {
        sub.unsubscribe?.();
        backgroundSubs.delete(delegatedRunId);
      }
    }
  };
}

function ensureBackgroundSubscription(
  delegatedRunId: string,
  subscribeFn: SubscribeFn,
): void {
  const existing = backgroundSubs.get(delegatedRunId);
  if (existing) {
    // If subscribe function changed (reconnect), replace the subscription
    if (existing.subscribeFn !== subscribeFn) {
      existing.unsubscribe?.();
      existing.unsubscribe = subscribeFn(
        [{ eventType: 'agent.run.*', runId: delegatedRunId, includeSubagents: true }],
        createBackgroundHandler(delegatedRunId),
      );
      existing.subscribeFn = subscribeFn;
    }
    existing.refCount++;
    return;
  }

  const unsub = subscribeFn(
    [{ eventType: 'agent.run.*', runId: delegatedRunId, includeSubagents: true }],
    createBackgroundHandler(delegatedRunId),
  );
  backgroundSubs.set(delegatedRunId, { refCount: 1, unsubscribe: unsub, subscribeFn });
}

function releaseBackgroundSubscription(delegatedRunId: string): void {
  const sub = backgroundSubs.get(delegatedRunId);
  if (!sub) return;
  sub.refCount--;
  // Only tear down if no more listeners AND task is terminal (no more events expected)
  const cached = delegatedTaskStateCache.get(delegatedRunId);
  if (sub.refCount <= 0 && cached?.status && isTerminalStatus(cached.status)) {
    sub.unsubscribe?.();
    backgroundSubs.delete(delegatedRunId);
  }
}

/**
 * Begin (ref-counted) a background subscription to a delegated run's live
 * `agent.run.*` events so its chain-of-thought accumulates in the shared cache
 * from the run's FIRST event — independent of when (or whether) its card mounts.
 *
 * `AuiTaskDashboard` calls this the moment a task is discovered, closing the gap
 * where the card subscribes only on mount (after discovery) and misses the
 * already-streamed events — which previously left the card relying on REST
 * polling for status. Cards read the same cache via `useDelegatedTaskStatus`,
 * so they render full progress even when mounted late. Returns a release fn.
 */
export function trackDelegatedRun(
  delegatedRunId: string,
  subscribe: SubscribeFn,
): () => void {
  ensureBackgroundSubscription(delegatedRunId, subscribe);
  return () => releaseBackgroundSubscription(delegatedRunId);
}

/** Clean up state cache entries that have no active background subscription and are terminal. */
export function clearDelegatedTaskCaches(): void {
  for (const [runId, state] of delegatedTaskStateCache) {
    if (!backgroundSubs.has(runId) || (state.status && isTerminalStatus(state.status))) {
      delegatedTaskStateCache.delete(runId);
    }
  }
}

/** @internal exported for unit tests */
export interface InternalState {
  status: DelegatedTaskStatus | null;
  activityPhase: TaskActivityPhase;
  activityText: string | null;
  toolCallCount: number;
  startedAt: number | null;
  completedAt: number | null;
  timelineEntries: DelegatedTimelineEntry[];
  entryCounter: number;
  pendingReasoningText: string | null;
  lastResponseText: string | null;
  usage: TokenUsage | null;
}

/** @internal exported for unit tests */
export const INITIAL_STATE: InternalState = {
  status: null,
  activityPhase: 'idle',
  activityText: null,
  toolCallCount: 0,
  startedAt: null,
  completedAt: null,
  timelineEntries: [],
  entryCounter: 0,
  pendingReasoningText: null,
  lastResponseText: null,
  usage: null,
};

/** @internal exported for unit tests */
export function applyEvent(state: InternalState, event: PlatformEvent): InternalState {
  const type = event.eventType;
  const d = event.data as Record<string, unknown>;

  // Sub-agent events have parentRunId — their terminal events should NOT
  // change the overall delegated run status. Delegated runs also carry
  // parentRunId (pointing to the operator) but are independent runs whose
  // status should update normally.
  const isSubagentEvent = !!d?.parentRunId && d?.executionMode !== 'delegated';

  // Sub-agent PROGRESS events (reasoning_*, response_*, tool_call,
  // final_answer_*) belong to a child run. They should update that child's
  // row in the rolling timeline rather than push entries on the parent's.
  // Lifecycle events (subagent_invoked / started / completed) are emitted
  // on the parent run (no parentRunId) and are handled by the main switch
  // below.
  if (isSubagentEvent) {
    return applySubAgentProgressEvent(state, event);
  }

  // Guard: if we receive any non-terminal event but missed the started event,
  // infer that the run is already running.
  const isTerminal = type === 'agent.run.completed' || type === 'agent.run.failed' || type === 'agent.run.cancelled';
  const base = state.status === null && !isTerminal
    ? { ...state, status: 'running' as const, startedAt: Date.now() }
    : state;

  switch (type) {
    case 'agent.run.started':
      // Only set started for the parent run, not sub-agents
      if (isSubagentEvent) return base;
      return {
        ...base,
        status: 'running',
        activityPhase: 'idle',
        startedAt: Date.now(),
      };

    case 'agent.run.reasoning_start':
    case 'agent.run.reasoning_delta':
      return {
        ...base,
        activityPhase: 'reasoning',
        activityText: type === 'agent.run.reasoning_delta'
          ? truncate(d.accumulated as string | undefined ?? d.delta as string | undefined, 120)
          : base.activityText,
        pendingReasoningText: type === 'agent.run.reasoning_delta'
          ? (d.accumulated as string | undefined ?? base.pendingReasoningText)
          : base.pendingReasoningText,
      };

    case 'agent.run.reasoning_stop': {
      const text = base.pendingReasoningText;
      const entries = text
        ? pushEntry(base.timelineEntries, {
            kind: 'narration',
            id: `dtl-${base.entryCounter}`,
            text: truncate(text, 120) ?? '',
          })
        : base.timelineEntries;
      return {
        ...base,
        activityPhase: 'idle',
        activityText: null,
        pendingReasoningText: null,
        timelineEntries: entries,
        entryCounter: text ? base.entryCounter + 1 : base.entryCounter,
      };
    }

    case 'agent.run.tool_call': {
      const toolName = (d.toolDisplayName as string) ?? (d.toolName as string) ?? 'tool';
      const rawToolName = (d.toolName as string) ?? toolName;
      const input = (d.input as Record<string, unknown> | undefined) ?? {};
      // Prefer the backend-authored label; fall back to the FE heuristic.
      const backendLabel = typeof d.label === 'string' && d.label ? d.label : undefined;
      const detail = backendLabel ?? getToolDetail(rawToolName, input) ?? undefined;
      return {
        ...base,
        activityPhase: 'tool_calling',
        activityText: detail ?? toolName,
        toolCallCount: base.toolCallCount + 1,
        timelineEntries: pushEntry(base.timelineEntries, {
          kind: 'tool-call',
          id: `dtl-${base.entryCounter}`,
          toolName: rawToolName,
          toolDisplayName: toolName,
          detail,
        }),
        entryCounter: base.entryCounter + 1,
      };
    }

    case 'agent.run.tool_result':
      return {
        ...base,
        activityPhase: 'idle',
        activityText: null,
      };

    case 'agent.run.response_start':
    case 'agent.run.response_delta':
    case 'agent.run.final_answer_start':
    case 'agent.run.final_answer_delta':
      return {
        ...base,
        activityPhase: 'responding',
        activityText: type.includes('delta')
          ? truncate(d.accumulated as string | undefined ?? d.delta as string | undefined, 120)
          : base.activityText,
        lastResponseText: type.includes('delta')
          ? (d.accumulated as string | undefined ?? base.lastResponseText)
          : base.lastResponseText,
      };

    case 'agent.run.response_stop':
    case 'agent.run.final_answer_stop': {
      const text = base.lastResponseText;
      const eventUsage = d.usage as TokenUsage | undefined;
      // Push the response text as a regular narration so the task-card
      // timeline shows what was actually said (e.g. "Now let me look at
      // the previous day's run…") instead of a generic "Producing output…"
      // line. Matches how the conversation CoT renders the same events.
      const entries = text
        ? pushEntry(base.timelineEntries, {
            kind: 'narration',
            id: `dtl-${base.entryCounter}`,
            text: truncate(text, 120) ?? '',
          })
        : base.timelineEntries;
      return {
        ...base,
        activityPhase: 'idle',
        activityText: null,
        timelineEntries: entries,
        entryCounter: text ? base.entryCounter + 1 : base.entryCounter,
        lastResponseText: null,
        usage: eventUsage ?? base.usage,
      };
    }

    case 'agent.run.completed': {
      const eventUsage = d.usage as TokenUsage | undefined;
      const responsePreview = d.responsePreview as string | undefined;
      // Sub-agent completed: aggregate usage but don't change parent status
      if (isSubagentEvent) {
        return {
          ...base,
          activityPhase: 'idle',
          activityText: null,
          usage: eventUsage ? mergeUsage(base.usage, eventUsage) : base.usage,
        };
      }
      return {
        ...base,
        status: 'completed',
        activityPhase: 'idle',
        activityText: null,
        completedAt: Date.now(),
        lastResponseText: responsePreview ?? base.lastResponseText ?? (d.response as string | undefined) ?? null,
        usage: eventUsage ? mergeUsage(base.usage, eventUsage) : base.usage,
      };
    }

    case 'agent.run.failed':
      // Sub-agent failure doesn't fail the parent run
      if (isSubagentEvent) return { ...base, activityPhase: 'idle', activityText: null };
      return {
        ...base,
        status: 'failed',
        activityPhase: 'idle',
        activityText: null,
        completedAt: Date.now(),
      };

    case 'agent.run.cancelled':
      // Sub-agent cancellation doesn't cancel the parent run
      if (isSubagentEvent) return { ...base, activityPhase: 'idle', activityText: null };
      return {
        ...base,
        status: 'cancelled',
        activityPhase: 'idle',
        activityText: null,
        completedAt: Date.now(),
      };

    case 'agent.run.subagent_invoked':
      // Announcement only; childRunIds aren't known yet. Wait for
      // subagent_returned below to push the rows.
      return base;

    case 'agent.run.subagent_returned': {
      // Parent reports that the spawned sub-agents now have runIds. Push a
      // `subagent-row` per child so the rolling timeline picks them up like
      // any other entry.
      const tasks = (d.tasks as Array<Record<string, unknown>> | undefined) ?? [];
      let entries = base.timelineEntries;
      for (const t of tasks) {
        const childRunId = (t.childRunId as string | undefined) ?? (t.runId as string | undefined);
        if (!childRunId) continue;
        const id = `sa-${childRunId}`;
        if (entries.some((e) => e.id === id)) continue;
        const child: SubAgentChild = {
          agentName: (t.agentName as string | undefined) ?? (t.agentId as string | undefined) ?? 'Sub-Agent',
          status: 'pending',
          items: [],
        };
        entries = [...entries, { kind: 'subagent-row', id, child }];
      }
      if (entries.length > MAX_TIMELINE_ENTRIES) entries = entries.slice(-MAX_TIMELINE_ENTRIES);
      return { ...base, timelineEntries: entries };
    }

    // NOTE: subagent_started / _completed carry parentRunId, so they are
    // routed to applySubAgentProgressEvent above (which creates/updates the
    // row). These main-switch cases only fire for the rare no-parentRunId
    // variant and act as a defensive fallback.
    case 'agent.run.subagent_started':
      return updateSubagentChild(base, (d.runId as string | undefined) ?? '', (c) => ({
        ...c,
        status: 'running',
      }));

    case 'agent.run.subagent_completed':
      return updateSubagentChild(base, (d.runId as string | undefined) ?? '', (c) => ({
        ...c,
        status: (d.success === false ? 'failed' : 'completed'),
      }));

    default:
      return base;
  }
}

/** Apply a sub-agent's own progress event (reasoning/response/tool_call/etc.)
 *  to the corresponding `subagent-row` entry. The row is updated IN PLACE —
 *  see {@link updateSubagentChild} for why we no longer reorder. */
function applySubAgentProgressEvent(state: InternalState, event: PlatformEvent): InternalState {
  const type = event.eventType;
  const d = event.data as Record<string, unknown>;
  const childRunId = (event.subjectId ?? (d.runId as string | undefined) ?? '');
  if (!childRunId) return state;

  // A child event can arrive before any `subagent_returned` seeded its row
  // (the live runtime streams subagent_started + progress directly) — create
  // the row so the activity isn't dropped. agentName is known on the lifecycle
  // events; progress events fall back to the placeholder until then.
  const base = ensureSubagentRow(state, childRunId, {
    agentName: d.agentName as string | undefined,
  });

  switch (type) {
    // Lifecycle events for a child carry parentRunId and are routed here (not
    // to applyEvent's main switch). Flip the row's status accordingly.
    case 'agent.run.subagent_started':
      return updateSubagentChild(base, childRunId, (c) => ({
        ...c,
        status: 'running',
        agentName: (d.agentName as string | undefined) ?? c.agentName,
      }));

    case 'agent.run.subagent_completed':
      return updateSubagentChild(base, childRunId, (c) => ({
        ...c,
        status: d.success === false ? 'failed' : 'completed',
        agentName: (d.agentName as string | undefined) ?? c.agentName,
      }));

    case 'agent.run.tool_call': {
      const displayName = (d.toolDisplayName as string | undefined) ?? (d.toolName as string | undefined) ?? 'tool';
      const rawToolName = (d.toolName as string | undefined) ?? displayName;
      const input = (d.input as Record<string, unknown> | undefined) ?? {};
      const backendLabel = typeof d.label === 'string' && d.label ? d.label : undefined;
      const detail = backendLabel ?? getToolDetail(rawToolName, input) ?? undefined;
      return updateSubagentChild(base, childRunId, (c) => ({
        ...c,
        items: [
          // Keep only the latest item — SubAgentChildRow only renders the
          // last one anyway, and discarding stale items prevents unbounded
          // growth across long-running children.
          { kind: 'tool-call', id: `${childRunId}-tc-${(d.toolCallId as string | undefined) ?? Date.now()}`, toolName: rawToolName, toolDisplayName: displayName, detail },
        ],
      }));
    }

    case 'agent.run.reasoning_stop': {
      const text = d.content as string | undefined;
      if (!text) return base;
      return updateSubagentChild(base, childRunId, (c) => ({
        ...c,
        items: [{ kind: 'narration', id: `${childRunId}-narr-${Date.now()}`, text: truncate(text, 80) ?? '' }],
      }));
    }

    case 'agent.run.response_stop':
    case 'agent.run.final_answer_stop': {
      const text = d.content as string | undefined;
      if (!text) return base;
      return updateSubagentChild(base, childRunId, (c) => ({
        ...c,
        items: [{ kind: 'narration', id: `${childRunId}-resp-${Date.now()}`, text: truncate(text, 80) ?? '' }],
        // final_answer_stop implies the child finished successfully; the
        // matching `subagent_completed` event from the parent will follow
        // and flip status to 'completed'. Don't pre-empt that here — keep
        // 'running' so the spinner stays until the parent confirms.
      }));
    }

    default:
      return base;
  }
}

/** Ensure a `subagent-row` exists for `childRunId`, appending one if absent.
 *
 *  The legacy lifecycle created rows up-front in the `subagent_returned` case.
 *  The live runtime instead streams `subagent_started` + the child's progress
 *  events with no `subagent_returned`, so the row has to be created lazily on
 *  first sight (mirrors the dedup in that case). Idempotent — safe to call from
 *  every sub-agent event. */
function ensureSubagentRow(
  state: InternalState,
  childRunId: string,
  seed: { agentName?: string },
): InternalState {
  if (!childRunId) return state;
  const id = `sa-${childRunId}`;
  if (state.timelineEntries.some((e) => e.id === id)) return state;
  const child: SubAgentChild = {
    agentName: seed.agentName ?? 'Sub-Agent',
    status: 'pending',
    items: [],
  };
  let entries: DelegatedTimelineEntry[] = [
    ...state.timelineEntries,
    { kind: 'subagent-row', id, child },
  ];
  if (entries.length > MAX_TIMELINE_ENTRIES) entries = entries.slice(-MAX_TIMELINE_ENTRIES);
  return { ...state, timelineEntries: entries };
}

/** Find a `subagent-row` entry by `sa-<childRunId>` and apply `update` to its
 *  child, keeping the row at its original position in the timeline.
 *
 *  This used to move ("bump") the row to the end on every update so the
 *  rolling-window slice would keep the most-active sub-agent visible. But in a
 *  bottom-aligned `slice(-N)` window that reordering is exactly what produced
 *  the reported bug: a sub-agent row appeared pinned to the bottom while newer
 *  parent entries inserted just above it. Updating in place (like the
 *  append-only conversation CoT) keeps the timeline in stable first-appearance
 *  order, so rows never visually jump. */
function updateSubagentChild(
  state: InternalState,
  childRunId: string,
  update: (child: SubAgentChild) => SubAgentChild,
): InternalState {
  const id = `sa-${childRunId}`;
  const idx = state.timelineEntries.findIndex((e) => e.id === id);
  if (idx === -1) return state;
  const existing = state.timelineEntries[idx];
  if (existing.kind !== 'subagent-row') return state;
  const entries = state.timelineEntries.slice();
  entries[idx] = { kind: 'subagent-row', id, child: update(existing.child) };
  return { ...state, timelineEntries: entries };
}

function mergeUsage(prev: TokenUsage | null, next: TokenUsage): TokenUsage {
  if (!prev) return next;
  return {
    inputTokens: prev.inputTokens + next.inputTokens,
    outputTokens: prev.outputTokens + next.outputTokens,
    totalTokens: prev.totalTokens + next.totalTokens,
  };
}

function truncate(text: string | undefined, max: number): string | null {
  if (!text) return null;
  return text.length > max ? text.slice(0, max) + '...' : text;
}

function pushEntry(entries: DelegatedTimelineEntry[], entry: DelegatedTimelineEntry): DelegatedTimelineEntry[] {
  // Skip consecutive identical tool-call labels — same dedup the
  // conversation CoT applies (`deriveTimelineEntries`).
  const last = entries[entries.length - 1];
  if (entry.kind === 'tool-call' && last && last.kind === 'tool-call') {
    if (toolCallEntryLabel(entry) === toolCallEntryLabel(last)) {
      return entries;
    }
  }
  const next = [...entries, entry];
  return next.length > MAX_TIMELINE_ENTRIES ? next.slice(-MAX_TIMELINE_ENTRIES) : next;
}

export interface InitialTaskSummary {
  status: DelegatedTaskStatus;
  durationMs?: number;
  toolCallCount?: number;
  usage?: TokenUsage;
  responsePreview?: string;
  error?: string;
  updatedAt?: string;
}

export function useDelegatedTaskStatus(
  delegatedRunId: string | undefined,
  initialSummary?: InitialTaskSummary,
): DelegatedTaskLiveStatus {
  const { subscribe, isConnected } = usePlatformEvents();
  const [state, setState] = useState<InternalState>(INITIAL_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [resolved, setResolved] = useState(false);
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);

  // Elapsed timer
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback((startedAt: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!delegatedRunId) return;

    // Seed from cache if available (remount scenario — preserves full accumulated state)
    const cached = delegatedTaskStateCache.get(delegatedRunId);
    if (cached && cached.status !== null) {
      setState(cached);
      setResolved(true);
      setLastEventAt(cached.completedAt ?? Date.now());
      if (cached.startedAt && !cached.completedAt) {
        startTimer(cached.startedAt);
        setElapsedMs(Date.now() - cached.startedAt);
      } else if (cached.startedAt && cached.completedAt) {
        setElapsedMs(cached.completedAt - cached.startedAt);
      }
    } else {
      setState(INITIAL_STATE);
      setElapsedMs(null);
      setResolved(false);
      setLastEventAt(null);
    }

    // Register listener for reactive updates from the background subscription
    stateListeners.set(delegatedRunId, (next) => {
      setResolved(true);
      setLastEventAt(Date.now());
      setState((prev) => {
        if (next.status === 'running' && prev.status !== 'running' && next.startedAt) {
          startTimer(next.startedAt);
        }
        if (next.completedAt && !prev.completedAt) {
          stopTimer();
          if (next.startedAt) {
            setElapsedMs(next.completedAt - next.startedAt);
          }
        }
        return next;
      });
    });

    // Ensure background subscription is active (stays alive across unmount/remount)
    ensureBackgroundSubscription(delegatedRunId, subscribe);

    return () => {
      stateListeners.delete(delegatedRunId);
      releaseBackgroundSubscription(delegatedRunId);
      stopTimer();
    };
  }, [delegatedRunId, subscribe, startTimer, stopTimer]);

  // Seed initial state — prefer cache (already handled above), then props summary, then pre-fetched store
  useEffect(() => {
    if (!delegatedRunId) return;

    // Skip seeding if cache already provided state (handled in the main useEffect above)
    const cached = delegatedTaskStateCache.get(delegatedRunId);
    if (cached && cached.status !== null) return;

    // 1. Props-level summary (most reliable, no timing issues)
    if (initialSummary && isTerminalStatus(initialSummary.status)) {
      const seededState: InternalState = {
        ...INITIAL_STATE,
        status: initialSummary.status,
        completedAt: initialSummary.updatedAt ? new Date(initialSummary.updatedAt).getTime() : Date.now(),
        toolCallCount: initialSummary.toolCallCount ?? 0,
        usage: initialSummary.usage ?? null,
        lastResponseText: initialSummary.error ?? initialSummary.responsePreview ?? null,
      };
      delegatedTaskStateCache.set(delegatedRunId, seededState);
      setResolved(true);
      setState((prev) => prev.status !== null ? prev : seededState);
      if (initialSummary.durationMs != null) setElapsedMs(initialSummary.durationMs);
      setLastEventAt(initialSummary.updatedAt ? new Date(initialSummary.updatedAt).getTime() : Date.now());
      return;
    }

    // 2. Check the summary store (populated by useConversationRestore for terminal tasks)
    const seeded = delegatedTaskSummaryStore.get(delegatedRunId);
    if (seeded && isTerminalStatus(seeded.status)) {
      const seededState: InternalState = {
        ...INITIAL_STATE,
        status: seeded.status,
        completedAt: seeded.updatedAt ? new Date(seeded.updatedAt).getTime() : Date.now(),
        toolCallCount: seeded.toolCallCount ?? 0,
        usage: seeded.usage ?? null,
        lastResponseText: seeded.error ?? seeded.responsePreview ?? null,
      };
      delegatedTaskStateCache.set(delegatedRunId, seededState);
      setResolved(true);
      setState((prev) => prev.status !== null ? prev : seededState);
      if (seeded.durationMs != null) setElapsedMs(seeded.durationMs);
      setLastEventAt(seeded.updatedAt ? new Date(seeded.updatedAt).getTime() : Date.now());
      return;
    }

    // No REST fallback — terminal tasks are resolved from props/store above,
    // running tasks get live updates via WebSocket.
    setResolved(true);
  }, [delegatedRunId, startTimer]);

  // Cleanup timer on unmount
  useEffect(() => stopTimer, [stopTimer]);

  const resultSummary = state.status === 'completed' || state.status === 'failed'
    ? truncate(state.lastResponseText ?? undefined, 200)
    : null;

  return {
    status: state.status,
    activityPhase: state.activityPhase,
    activityText: state.activityText,
    toolCallCount: state.toolCallCount,
    elapsedMs,
    isConnected,
    timelineEntries: state.timelineEntries,
    resultSummary,
    usage: state.usage,
    resolved,
    lastEventAt,
  };
}
